/**
 * @description Services from the flows within the toolbar
 * @author Daniel Duque Usma
 */

import axios from 'axios'
import appRequest from '../config.json'
import { HubConnectionBuilder } from '@aspnet/signalr'

let likesAbortSignal = null

/**
 *@description Get counter from all apps
 */
export function getCountPerApp () {
  return axios({
    method: 'GET',
    url: appRequest.endPoints.counterApps,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('sessionToken')}`
    }
  })
}

/**
 *@description Init user processes from the backend side
 */
export function initUserProcesses () {
  return axios({
    method: 'GET',
    url: appRequest.endPoints.userInit,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('sessionToken')}`
    }
  })
}

/**
 * @description Get My Apps
 */
export function getMyApps () {
  return axios({
    method: 'GET',
    url: `${appRequest.endPoints.toolbarService}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('sessionToken')}`
    }
  })
}

/**
 * @description Reorder my apps
 * @param apps reordered apps list
 */
export function reorderMyApps (apps) {
  return axios({
    method: 'PATCH',
    url: `${appRequest.endPoints.toolbarService}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('sessionToken')}`
    },
    data: apps
  })
}

/**
 * @description Restore my apps order to default
 */
export function restoreOrderMyApps () {
  return axios({
    method: 'PATCH',
    url: `${appRequest.endPoints.toolbarService}/restore`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('sessionToken')}`
    }
  })
}

/**
 *@description Request to open connection with WebSockets
 *@returns {connection} Object with its methods
 */
export function openConnectionWs () {
  const connection = new HubConnectionBuilder()
    .withUrl(`${appRequest.endPoints.notificationsService}notificationshub`, { accessTokenFactory: () => `${localStorage.getItem('sessionToken')}` })
    .build()
  return connection
}

/**
 *@description Start connection with Web Socket
 *@param connection object
 */
export function startConnection (connection) {
  let startedPromise = null
  function start () {
    startedPromise = connection.start().catch(err => {
      console.error('Failed to connect with hub', err)
      return new Promise((resolve, reject) => setTimeout(() => start().then(resolve).catch(reject), 5000))
    })
    return startedPromise
  }
  start()
}

/**
 * @description Load notifications list
 */
export function loadNotifications () {
  return axios({
    method: 'GET',
    url: `${appRequest.endPoints.notificationsService}notifications/latest`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
      'Cache-Control': 'no-cache, max-age=0, no-store'
    }
  })
}

/**
 * @description Mark notification as read
 * @param id id of the notification
 */
export function markAsReadService (id) {
  return axios({
    method: 'PATCH',
    url: `${appRequest.endPoints.notificationsService}notifications/read/${id}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('sessionToken')}`
    }
  })
}

/**
 * @description Remove notification from latest
 * @param id id of the notification
 */
export function removeNotificationService (id) {
  return axios({
    method: 'PATCH',
    url: `${appRequest.endPoints.notificationsService}notifications/hide/${id}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('sessionToken')}`
    }
  })
}

/**
 * @description Mark notifications as read
 */
export function markAllAsReadService () {
  return axios({
    method: 'PATCH',
    url: `${appRequest.endPoints.notificationsService}notifications/readAll`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('sessionToken')}`
    }
  })
}

/**
 * @description Process get profile picture url
 * @param ecCode Employe code to send and return the url
 */
export function getProfilePicture (ecCode) {
  return new Promise((resolve, reject) => {
    return axios({
      method: 'GET',
      url: `${appRequest.endPoints.peopleServices}profilepicture/${ecCode}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('sessionToken')}`
      }
    }).then((response) => {
      resolve(response)
    },
    (error) => {
      reject(error)
    })
  })
}

/**
 * @description Searches a term to retrieve a list of users that match with the term.
 * @param searchTerm term for searching a user.
 * @param numberOfResults Amount of expected results.
 */
export function searchUser (searchTerm, numberOfResults) {
  const results = numberOfResults ? `&top=${numberOfResults}` : ''
  const url = `${appRequest.endPoints.suggestionsService}suggestions/people?term=${searchTerm}${results}`
  return new Promise((resolve, reject) => {
    axios({
      method: 'GET',
      url: url
    }).then(
      (response) => {
        resolve(response.data)
      },
      (error) => {
        console.error(error)
        reject(error)
      }
    )
  })
}

/**
 * @description Request to get the users who are co-owners of a collection
 * @param collectionId Current collection
 * @param size Size of pagination
 * @param page page of pagination
 */
export function getCollectionCoOwners (collectionId, size, page) {
  return new Promise((resolve, reject) => {
    axios({
      method: 'GET',
      url: `${appRequest.endPoints.collections}/${collectionId}/coOwners/${page}/${size}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('sessionToken')}`
      }
    }).then((response) => {
      resolve(response.data)
    }, (error) => {
      reject(error)
    })
  })
}

/**
 * @description Request the duplicate action of a collection.
 * @param collectionId Id of te collection to duplicate
 */
export function getCollectionDuplicateInfo (collectionId) {
  return new Promise((resolve, reject) => {
    axios({
      method: 'GET',
      url: `${appRequest.endPoints.collections}/${collectionId}/duplicate`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('sessionToken')}`
      }
    }).then(
      (response) => {
        resolve(response)
      },
      (error) => {
        reject(error)
      }
    )
  })
}

/**
 * @description Create New Collection
 * @param data Object to create collection
 */
export function createNewCollection (data) {
  return new Promise((resolve, reject) => {
    axios({
      method: 'POST',
      url: appRequest.endPoints.collections,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('sessionToken')}`
      },
      data: data
    }).then(
      response => {
        resolve(response)
      },
      (error) => {
        console.error(error)
        reject(error)
      }
    )
  })
}

/**
 * @description Request the edition of a collection own by the current user
 * @param data Contains info to edit the collection
 * @param collectionId Id of te collection to edit
 */
export function editCollectionbyId (data, collectionId) {
  return new Promise((resolve, reject) => {
    axios({
      method: 'PATCH',
      url: `${appRequest.endPoints.collections}/${collectionId}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('sessionToken')}`
      },
      data: data
    }).then(
      (response) => {
        resolve(response)
      },
      (error) => {
        reject(error)
      }
    )
  })
}

/**
 * @description Duplicate a collection
 * @param data Contains info to create a new collection
 */
export function duplicateNewCollection (data) {
  return new Promise((resolve, reject) => {
    axios({
      method: 'POST',
      url: `${appRequest.endPoints.collections}/duplicate`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('sessionToken')}`
      },
      data: data
    }).then(
      (response) => {
        resolve(response)
      },
      (error) => {
        reject(error)
      }
    )
  })
}

/**
 * @description Get users info by email
 * @param emails user emails to get the data
 */
export function getUsersDataByEmail (emails) {
  const url = `${appRequest.endPoints.suggestionsService}suggestions/people/info`
  return new Promise((resolve, reject) => {
    axios({
      method: 'POST',
      url: url,
      data: {
        emails: emails,
        useLowerCase: true
      }
    }).then(
      (response) => {
        resolve(response.data)
      },
      (error) => {
        console.error(error)
        reject(error)
      }
    )
  })
}

/**
 * @description Add or remove item from a collection
 * @param collectionId Id collection.
 * @param action Action item is going to do (add or remove)
 * @param cardsId Id item that will be added or removed from the collection
 * @param analyticData information about the application and the section where the action is called
 */
export function addRemoveItemsFromCollection (collectionId, action, cardsId, analyticData = null) {
  return new Promise((resolve, reject) => {
    axios({
      method: 'PATCH',
      url: analyticData ? `${appRequest.endPoints.collections}/${collectionId}/${action}?application=${analyticData.application}&applicationSection=${analyticData.applicationSection}` : `${appRequest.endPoints.collections}/${collectionId}/${action}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('sessionToken')}`
      },
      data: {
        items: cardsId
      }
    }).then(
      (response) => {
        resolve(response)
      },
      (error) => {
        console.error(error)
        reject(error)
      }
    )
  })
}

/**
 * @description Request to get the users that liked an article
 * @param itemId the article id
 * @param page the page number
 */
export function getItemLikes (itemId, page) {
  return new Promise((resolve, reject) => {
    const signal = axios.CancelToken.source()
    likesAbortSignal = signal

    axios({
      method: 'GET',
      url: `${appRequest.endPoints.notificationsService}SocialMedia/Like/${itemId}/Users/${page}/12`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('sessionToken')}`
      },
      cancelToken: signal.token
    }).then(
      (response) => {
        resolve(response.data)
      },
      (error) => {
        console.error(error)
        reject(error)
      }
    )
  })
}

/**
 * @description Cancels get likes request
 */
export function cancelLikesRequest () {
  likesAbortSignal.cancel()
}
 
/**
 * @description Get information of a highlight
 * @param highlightId id of the highlight
 */
export function getHighlightInformation (highlightId) {
  return new Promise((resolve, reject) => {
    axios({
      method: 'GET',
      url: `${appRequest.endPoints.goService}HighlightsDetail/${highlightId}?application=Search&applicationSection=ResultCard`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('sessionToken')}`
      }
    }).then(
      (response) => {
        resolve(response)
      },
      (error) => {
        reject(error)
      }
    )
  })
}
 
/**
 * @description gets social media info of a single article by id
 * @param application application to track
 * @param applicationSection applicationSection to track
 * @param action action to track
 * @param itemId itemId to track
 */
export function saveAnalytics (application, applicationSection, action, itemId) {
  return new Promise((resolve, reject) => {
    axios({
      method: 'POST',
      url: `${appRequest.endPoints.goService}analytics`,
      data: {
        application: application,
        applicationSection: applicationSection,
        action: action,
        id: itemId || null
      },
      headers: {
        Authorization: `Bearer ${localStorage.getItem('sessionToken')}`
      }
    }).then(
      (response) => {
        resolve(response)
      },
      (error) => {
        reject(error)
      }
    )
  })
}

/**
 * @description Request to delete a comment.
 * @param itemId id of the item.
 * @param commentId id of the comment.
 */
 export function deleteComment (itemId, commentId) {
  return new Promise((resolve, reject) => {
    axios({
      method: 'DELETE',
      url: `${appRequest.endPoints.notificationsService}SocialMedia/Comments`,
      data: {
        itemId: itemId,
        commentId: commentId
      },
      headers: {
        Authorization: `Bearer ${localStorage.getItem('sessionToken')}`
      }
    }).then(
      (response) => {
        resolve(response)
      },
      (error) => {
        reject(error)
      }
    )
  })
}